import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import IconButton from '@mui/material/IconButton';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import React, { useEffect, useState, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { DialogContent, DialogContentText, DialogTitle, Box, styled } from '@mui/material';
import { isEligibleForPasskeyPrompt } from 'app/auth/passkeyPromptSession';
import { consumeSsoFailure, subscribeSsoFailure } from 'app/auth/ssoErrorSignal';
import { isRepeatSwitchReturn, openMicrosoftSignOutPage } from 'app/auth/ssoAccountVerification';
import {
  classifySsoError,
  extractSsoIdentity,
  getPendingSsoAttempt,
  resolveSsoAttempt,
  ssoIdentityLineKey,
} from 'app/auth/ssoAttemptLog';
import { CognitoError, selectAuth } from 'app/store/authSlice';
import ResponsiveDialog from 'app/shared-components/layout/dialog/ResponsiveDialog';
import LoadingOverlay from 'app/shared-components/util/LoadingOverlay';
import SnackbarCloseButton from 'app/shared-components/util/SnackbarCloseButton';
import { useLoginFlow } from 'app/hooks/useLoginFlow';
import { trimText } from 'util/sanitizeText';
import AuthSplitLayout from 'app/pages/auth/shared/AuthSplitLayout';
import AuthTextField from 'app/pages/auth/shared/AuthTextField';
import AuthButton from 'app/pages/auth/shared/AuthButton';
import AuthCheckbox from 'app/pages/auth/shared/AuthCheckbox';
import { authColors, authFontFamily, authType } from 'app/pages/auth/shared/authTheme';
import { replayChrome, sentryMask } from 'sentryReplayPrivacy';

/**
 * Login screen — redesigned to the Figma "Web-designs" Material 3 spec
 * (nodes 4377:33473 email step / 4377:33544 password step). All auth logic
 * (SSO detection, SAML callback errors, Cognito error handling, remember-me)
 * is preserved from useLoginFlow; only the presentation changed.
 */
function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const { username: rememberedUsername } = useSelector(selectAuth);
  const [isPending, setIsPending] = useState(false);
  const [authError, setAuthError] = useState();
  const [supportAlertOpen, setSupportAlertOpen] = useState(false);
  const infoSnackbarKey = useRef(null);
  const { t } = useTranslation();

  const {
    step,
    email: loginEmail,
    isCheckingSSO,
    isResolvingUsername,
    ssoError,
    handleEmailSubmit,
    handlePasswordSubmit,
    resetToEmailStep,
    startAccountSwitch,
  } = useLoginFlow();

  const [searchParams, setSearchParams] = useSearchParams();

  // Local form state
  const [emailValue, setEmailValue] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  // SEE-1170: set when an SSO login authenticated at Microsoft but the account
  // isn't provisioned in Speedchain; swaps the form for an explanatory message.
  // Amplify delivers this failure on the Hub (not the URL); wired up in an
  // effect below (both a subscription and a latch check, to catch a failure
  // that fired before this component mounted).
  const [ssoNotProvisioned, setSsoNotProvisioned] = useState(false);
  // SEE-1251: recovery context for the not-provisioned screen — the email the
  // user typed, the identity Microsoft actually authenticated (when the gate
  // reports it), and whether the Microsoft sign-out escape applies.
  const [ssoFailureDetail, setSsoFailureDetail] = useState(null);

  // One handler for every SSO failure, whichever way it arrives — as URL
  // params (mobile shell forwarding, hand-built links) or on the Amplify Hub
  // (web hosted-UI flow, where Amplify consumes the OAuth callback and the
  // URL never carries the error).
  const handleSsoFailure = (errorDescription) => {
    // SEE-1251: capture the attempt context BEFORE resolving clears it — the
    // typed email and IdP type feed the recovery UI on the error screen.
    const pending = getPendingSsoAttempt();
    // SEE-1200: terminate the pending SSO attempt log with the real reason.
    const failureCode = classifySsoError(errorDescription);
    resolveSsoAttempt('failure', { failureCode, reason: errorDescription });

    // User was invited but hasn't completed registration — redirect to signup
    if (failureCode === 'registration_required') {
      // Cognito wraps Lambda errors and appends a period ("PreSignUp failed
      // with error REGISTRATION_REQUIRED: a@b.com.") — strip it, or the email
      // fails validation in Register's disabled field and bricks the form.
      const ssoEmail = errorDescription.split('REGISTRATION_REQUIRED:')[1]?.trim().replace(/\.+$/, '');
      if (ssoEmail) {
        enqueueSnackbar('Please complete your account registration before signing in with SSO.', {
          variant: 'info',
          persist: true,
          action: (key) => <SnackbarCloseButton snackbarKey={key} />,
        });
        // REGISTRATION_REQUIRED can only come out of an SSO sign-in, so tell
        // Register directly (?sso=1) instead of making it re-derive this with
        // a network call — that check signs with IAM credentials whose state
        // is unreliable right after a failed SSO redirect (stale session or
        // freshly cleared), and a silent miss shows the password form.
        navigate(`/register/${encodeURIComponent(ssoEmail)}?sso=1`);
        return;
      }
    }

    // Missing profile info in the SSO provider — the user needs to fix this with their IT admin
    if (failureCode === 'missing_profile_attributes') {
      enqueueSnackbar(
        'Your company SSO account is missing required information (first name or last name). Please contact your IT administrator to update your profile.',
        {
          variant: 'error',
          persist: true,
          action: (key) => <SnackbarCloseButton snackbarKey={key} />,
        }
      );
      return;
    }

    // SAML configuration issue — not the user's fault, they need to report it
    if (failureCode === 'invalid_saml') {
      enqueueSnackbar(
        'There was a problem with your company sign-in configuration. Please contact support.',
        {
          variant: 'error',
          persist: true,
          action: (key) => <SnackbarCloseButton snackbarKey={key} />,
        }
      );
      return;
    }

    // SSO authenticated at Microsoft but the account isn't provisioned in
    // Speedchain — the PreSignup gate threw "User does not have access to the
    // application". Show a clear message instead of silently bouncing (the
    // recurring "wrong Microsoft account" reports). Safe by construction: this
    // fires only on the gate's rejection, which a successful login never
    // carries, and it keys off the provisioning result — never an email
    // comparison — so SSO users whose IdP NameID differs from their Speedchain
    // email are unaffected. Unlike the pre-auth domain check, revealing this
    // after a completed Microsoft sign-in is not a user-discovery vector: the
    // caller has already proven they own the account.
    if (failureCode === 'not_provisioned') {
      const typedEmail = (pending?.email || '').toLowerCase().trim();
      const returnedIdentity = extractSsoIdentity(errorDescription);
      setSsoFailureDetail({ typedEmail, returnedIdentity });
      setSsoNotProvisioned(true);
      return;
    }

    // For all other SSO errors, silently fall back to the password step.
    // Don't reveal why SSO failed — prevents user discovery attacks.
  };

  // Parse SSO error from URL (Cognito redirects back with ?error_description=...)
  useEffect(() => {
    const errorDescription = searchParams.get('error_description');
    if (!errorDescription) return;

    setSearchParams({}, { replace: true });
    handleSsoFailure(errorDescription);
    // handleSsoFailure is stable per render and intentionally not a dep: this
    // effect must run only when the URL params change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    // Subscribe first, then check the latch — so a failure that fired before
    // this effect ran is still caught, and one that fires later hits the
    // subscription. On web the failure arrives HERE (Amplify consumes the
    // OAuth callback and reports on the Hub); the URL-params effect above
    // covers the mobile shell's forwarding.
    const unsubscribe = subscribeSsoFailure(handleSsoFailure);
    const latched = consumeSsoFailure();
    if (latched) {
      handleSsoFailure(latched);
    }
    return unsubscribe;
    // Mount-only by design; handleSsoFailure closes over stable snackbar/nav.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (rememberedUsername) {
      setEmailValue(rememberedUsername);
    }
  }, [rememberedUsername]);

  useEffect(() => {
    if (authError?.name === CognitoError.PasswordReset) {
      enqueueSnackbar(t('passwordMustBeChanged'));
      navigate(`/forgot-password/${loginEmail}`);
      return;
    }
    // Unconfirmed account: send them to the verification screen (which resends
    // the code) instead of a dead-end error. Unconfirmed accounts are new
    // registrations, where the username IS the email (SEE-223), so the email
    // is a valid /verify-user param. Mirrors the forgot-password recovery.
    if (authError?.name === CognitoError.UserNotConfirmed) {
      enqueueSnackbar(t('accountNotVerified'), { variant: 'info' });
      navigate(`/verify-user/${encodeURIComponent(loginEmail)}`);
    }
  }, [authError, enqueueSnackbar, navigate, t, loginEmail]);

  useEffect(() => {
    if (
      !authError ||
      authError.name === CognitoError.PasswordReset ||
      authError.name === CognitoError.UserNotConfirmed
    ) {
      return;
    }

    // Sign-in errors (wrong password, locked out, lambda failure, etc.) only
    // ever originate from the password step — the email is already locked in by
    // then. Always surface them on the password field. The old single-form
    // login could route errors to the email field; on the two-step form that
    // field is disabled with no error slot, so such messages were invisible
    // (a wrong password appeared to do nothing).
    const message =
      authError.name === CognitoError.LambdaError ? t('accountSetupIncomplete') : authError.message;
    setPasswordError(message);
  }, [authError, t]);

  useEffect(
    () => () => {
      if (infoSnackbarKey.current) {
        closeSnackbar(infoSnackbarKey.current);
      }
    },
    [closeSnackbar]
  );

  const validateEmail = (value) => {
    if (!value || !value.trim()) {
      return t('enterEmail');
    }
    return '';
  };

  const validatePassword = (value) => {
    if (!value) {
      return t('enterPassword');
    }
    if (value.length < 8) {
      return t('passwordTooShort');
    }
    return '';
  };

  const onEmailSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    const error = validateEmail(emailValue);
    if (error) {
      setEmailError(error);
      return;
    }
    await handleEmailSubmit(emailValue, remember);
  };

  const onPasswordSubmit = async (e) => {
    e.preventDefault();
    if (infoSnackbarKey.current) {
      closeSnackbar(infoSnackbarKey.current);
      infoSnackbarKey.current = null;
    }

    setPasswordError('');
    const error = validatePassword(password);
    if (error) {
      setPasswordError(error);
      return;
    }

    setIsPending(true);
    const result = await handlePasswordSubmit(password, remember);
    setIsPending(false);

    if (result.error) {
      setAuthError(result.error);
      return;
    }
    if (result.usedDeprecatedUsername) {
      enqueueSnackbar(
        'Success! Just a heads up: We are retiring usernames soon. Please use your email to log in next time.',
        {
          variant: 'info',
          persist: true,
          action: (key) => <SnackbarCloseButton snackbarKey={key} />,
        }
      );
    }
    // SEE-213: route eligible users straight to the passkey prompt. Going via
    // /landing would hold them on a spinner while companies + feature flags
    // load — data the prompt doesn't need (it renders on auth alone; the
    // dashboard data keeps loading in the background meanwhile). Forward the
    // deep-link the user was originally headed to (if any) so the prompt can
    // continue there instead of dropping them on /landing.
    if (isEligibleForPasskeyPrompt(result.user)) {
      // SEE-1198: keep the query string so a bookmarked deep link (e.g.
      // advanced-filter URLs) survives the passkey hop, not just the pathname.
      const fromLocation = location.state?.from;
      const from = fromLocation ? `${fromLocation.pathname}${fromLocation.search ?? ''}` : undefined;
      navigate('/setup-passkey', { replace: true, state: from ? { from } : undefined });
    }
  };

  const onBackToEmail = () => {
    resetToEmailStep();
    setPassword('');
    setPasswordError('');
    setEmailError('');
    setAuthError(undefined);
  };

  if (isPending || step === 'sso-redirect') {
    return <LoadingOverlay isLoading={true} blockInteraction={true} />;
  }

  if (ssoNotProvisioned) {
    const identityLineKey = ssoIdentityLineKey(
      ssoFailureDetail?.typedEmail,
      ssoFailureDetail?.returnedIdentity
    );
    return (
      <AuthSplitLayout title={t('ssoNotProvisionedTitle')}>
        {/* SEE-1251: name the account the IdP actually authenticated. When it
            differs from what the user typed that IS the wrong-account loop and
            we name both; otherwise the address alone is what the user and
            support need to see. */}
        {identityLineKey && (
          <SsoErrorBody>
            <Trans
              i18nKey={identityLineKey}
              values={{
                typed: ssoFailureDetail?.typedEmail,
                returned: ssoFailureDetail?.returnedIdentity,
              }}
              components={{ b: <SsoIdentityEmphasis {...sentryMask} /> }}
            />
          </SsoErrorBody>
        )}
        <SsoErrorBody>{t('ssoNotProvisionedBody')}</SsoErrorBody>
        {/* SEE-1350: sign the wrong Microsoft account out and retry with the
            email the user typed. Hidden when the typed email didn't survive
            the redirect (nothing to retry with). When a switch attempt just
            came back as the same wrong account, relaunching again would loop —
            escalate to Microsoft's own sign-out page first. */}
        {ssoFailureDetail?.typedEmail &&
          (isRepeatSwitchReturn(ssoFailureDetail?.returnedIdentity) ? (
            <>
              <AuthButton type="button" onClick={() => openMicrosoftSignOutPage()}>
                {t('ssoMismatch.signOutOfMicrosoft', 'Sign out of Microsoft')}
              </AuthButton>
              <AuthButton
                type="button"
                variant="outlined"
                onClick={() => {
                  const typed = ssoFailureDetail.typedEmail;
                  const returned = ssoFailureDetail.returnedIdentity;
                  setSsoNotProvisioned(false);
                  setSsoFailureDetail(null);
                  startAccountSwitch(typed, returned);
                }}
              >
                {t('ssoMismatch.signedOutTryAgain', "I've signed out — try again")}
              </AuthButton>
            </>
          ) : (
            <AuthButton
              type="button"
              onClick={() => {
                const typed = ssoFailureDetail.typedEmail;
                const returned = ssoFailureDetail.returnedIdentity;
                setSsoNotProvisioned(false);
                setSsoFailureDetail(null);
                startAccountSwitch(typed, returned);
              }}
            >
              {t('ssoUseDifferentAccountButton', 'Use a different Microsoft account')}
            </AuthButton>
          ))}
        <AuthButton
          type="button"
          /* Outlined only when it sits under another button; when no typed
             email survived the redirect this is the screen's only action, so
             it takes the primary style (mirrors MobileSsoErrorScreen). */
          variant={ssoFailureDetail?.typedEmail ? 'outlined' : 'contained'}
          onClick={() => {
            setSsoNotProvisioned(false);
            setSsoFailureDetail(null);
            onBackToEmail();
          }}
        >
          {t('ssoNotProvisionedButton')}
        </AuthButton>
      </AuthSplitLayout>
    );
  }

  const adornmentSx = { color: authColors.textMuted, p: 0.5 };

  return (
    <AuthSplitLayout title={t('login')} onNeedHelp={() => setSupportAlertOpen(true)}>
      {step === 'email' && (
        <Box component="form" noValidate onSubmit={onEmailSubmit} sx={formSx} {...sentryMask}>
          <Box sx={fieldGroupSx}>
            <AuthTextField
              name="email"
              label={t('email')}
              type="text"
              // "username" (not "email") is what makes the browser/password
              // manager offer SAVED LOGIN accounts on this field, paired with
              // the "current-password" field on the next step. "email" marks it
              // a generic email field and suppresses saved-login suggestions.
              autoComplete="username"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={emailValue}
              onChange={(value) => {
                setEmailValue(value);
                setEmailError('');
              }}
              onBlur={(e) => setEmailValue(trimText(e.target.value))}
              error={emailError || ssoError}
            />
            <AuthCheckbox checked={remember} onChange={setRemember} label={t('rememberMe')} />
          </Box>
          <AuthButton type="submit" disabled={!emailValue.trim()} loading={isCheckingSSO}>
            {t('next')}
          </AuthButton>
        </Box>
      )}

      {step === 'password' && (
        <Box component="form" noValidate onSubmit={onPasswordSubmit} sx={formSx} {...sentryMask}>
          <Box sx={fieldGroupSx}>
            <AuthTextField
              name="email"
              label={t('email')}
              type="text"
              value={emailValue}
              onChange={() => {}}
              disabled
              endAdornment={
                <IconButton
                  onClick={onBackToEmail}
                  size="small"
                  aria-label={t('changeEmail')}
                  sx={adornmentSx}
                >
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
              }
            />
            <AuthTextField
              name="password"
              label={t('password')}
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              // eslint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
              value={password}
              onChange={(value) => {
                setPassword(value);
                setPasswordError('');
              }}
              onBlur={(e) => setPassword(trimText(e.target.value))}
              error={passwordError}
              trailingLink={{
                text: t('forgotPassword'),
                // Carry the email the user just typed so the reset screen
                // pre-fills it, instead of falling back to the last-remembered
                // account. Passed via router state (not a :username route param
                // — that path deliberately jumps straight to the code step).
                onClick: () => navigate('/forgot-password', { state: { email: emailValue } }),
              }}
              endAdornment={
                <IconButton
                  onClick={() => setShowPassword((s) => !s)}
                  size="small"
                  aria-label={showPassword ? t('hidePassword') : t('showPassword')}
                  sx={adornmentSx}
                >
                  {showPassword ? (
                    <VisibilityOutlinedIcon fontSize="small" />
                  ) : (
                    <VisibilityOffOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              }
            />
          </Box>
          <AuthButton type="submit" disabled={!password} loading={isPending || isResolvingUsername}>
            {t('login')}
          </AuthButton>
        </Box>
      )}

      {/* Outside the step blocks: invited users arrive on either step. */}
      <InviteCodeLink type="button" onClick={() => navigate('/onboard/invitation')} {...replayChrome}>
        {t('haveInviteCode')}
      </InviteCodeLink>

      <ResponsiveDialog open={supportAlertOpen} onClose={() => setSupportAlertOpen(false)}>
        <DialogTitle>{t('needHelp')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('contactUsAt')}{' '}
            <Link
              to="#"
              onClick={(e) => {
                window.location.href = 'mailto:support@speedchain.com';
                e.preventDefault();
              }}
            >
              support@speedchain.com
            </Link>
          </DialogContentText>
        </DialogContent>
        <Button
          className="mr-16 mb-16"
          sx={{ alignSelf: 'flex-end' }}
          onClick={() => setSupportAlertOpen(false)}
        >
          OK
        </Button>
      </ResponsiveDialog>
    </AuthSplitLayout>
  );
}

const SsoErrorBody = styled('p')({
  ...authType.body,
  fontFamily: authFontFamily,
  color: authColors.textMuted,
  textAlign: 'center',
  width: '100%',
  margin: 0,
});

// Identities get their own full line (never a mid-word break inside prose)
// in strong dark text so the account names are the most legible thing on the
// screen. Shared shape with SsoAccountMismatchScreen.
export const SsoIdentityEmphasis = styled('strong')({
  display: 'block',
  color: authColors.textStrong,
  fontWeight: 700,
  overflowWrap: 'anywhere',
  margin: '6px 0',
});

const InviteCodeLink = styled('button')({
  background: 'none',
  border: 'none',
  padding: 4,
  cursor: 'pointer',
  alignSelf: 'center',
  ...authType.helper,
  fontFamily: authFontFamily,
  color: authColors.textMuted,
  textDecoration: 'underline',
});

const formSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '32px',
  width: '100%',
};

const fieldGroupSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  width: '100%',
};

export default Login;
